import type { MaskInputOptions } from 'maska';

/**
 * Collection of utility functions for formatting values with masks
 * 
 * @example
 * ```typescript
 * import { masks } from '@ecx/ui';
 * 
 * // Phone: +55 (11) 99999-9999
 * masks.phone('5511999999999')
 * 
 * // CPF: 123.456.789-00
 * masks.cpf('12345678900')
 * 
 * // CNPJ: 12.345.678/0001-00
 * masks.cnpj('12345678000100')
 * 
 * // CEP: 01310-100
 * masks.cep('01310100')
 * 
 * // Date format: 01/12/2024 (from 2024-12-01)
 * masks.formatDate('2024-12-01')
 * ```
 */
export const masks = {
  /**
   * Formats a phone number with country code
   * @param value - Raw phone number (e.g., '5511999999999')
   * @returns Formatted phone (e.g., '+55 (11) 99999-9999')
   */
  phone: (value: string): string => 
    value.replace(/(\d{2})?(\d{2})?(\d{4,5})?(\d{4})/g, '+$1 ($2) $3-$4'),

  /**
   * Formats a CPF number
   * @param value - Raw CPF (e.g., '12345678900')
   * @returns Formatted CPF (e.g., '123.456.789-00')
   */
  cpf: (value: string): string => 
    value.replace(/(\d{3})?(\d{3})?(\d{3})?(\d{2})/g, '$1.$2.$3-$4'),

  /**
   * Formats a CNPJ number
   * @param value - Raw CNPJ (e.g., '12345678000100')
   * @returns Formatted CNPJ (e.g., '12.345.678/0001-00')
   */
  cnpj: (value: string): string => 
    value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5'),

  /**
   * Removes slashes from date and rearranges format
   * @param value - Date with slashes (e.g., '01/12/')
   * @returns Rearranged date (e.g., '12/01/')
   */
  removeSlashFromDate: (value: string): string => 
    value.replace(/(\d+[/])(\d+[/])/, '$2$1'),

  /**
   * Formats a date from ISO format to DD/MM/YYYY
   * @param value - ISO date (e.g., '2024-12-01')
   * @returns Formatted date (e.g., '01/12/2024')
   */
  formatDate: (value: string): string => {
    if (!value) return '';
    const [year, month, day] = value.split('-');
    if (year && month && day) {
      return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
    }
    return value;
  },

  /**
   * Formats a CEP (Brazilian postal code)
   * @param value - Raw CEP (e.g., '01310100')
   * @returns Formatted CEP (e.g., '01310-100')
   */
  cep: (value: string): string => 
    value.replace(/(\d{5})(\d{3})/, '$1-$2'),
};

export type MaskPattern = string | string[] | MaskInputOptions;

/**
 * Input mask patterns for use with v-maska directive
 * 
 * @example
 * ```vue
 * <input v-maska :data-maska="maskPatterns.cpf" />
 * <input v-maska :data-maska="maskPatterns.phone" />
 * ```
 *
 */
export const maskPatterns = {
  /** CPF mask: 000.000.000-00 */
  cpf: '###.###.###-##',
  
  /** CNPJ mask: 00.000.000/0000-00 */
  cnpj: '##.###.###/####-##',
  
  /** CEP mask: 00000-000 */
  cep: '#####-###',
  
  /** Phone mask Brazil: +55 fixo; usuário digita só DDD + número. Valor exibido inclui +55 para onlyDigits(). */
  phone: ['+55 (##) ####-####', '+55 (##) #####-####'],
  
  /** Mobile phone mask: (00) 00000-0000 */
  mobile: ['(##) ####-####', '(##) #####-####'],
  
  /** Date mask: 00/00/0000 */
  date: '##/##/####',
  
  /** Money mask: 0.000.000,00 */
  money: '#.###.###,##',
  
  /** Credit card mask: 0000 0000 0000 0000 */
  creditCard: '#### #### #### ####',

  /**
   * Percentual 0–100 com até 2 casas decimais (pt-BR).
   * O % é exibido apenas como suffix no EcxInput.
   */
  percentage: {
    mask: 'number',
    number: { locale: 'pt-BR', fraction: 2, unsigned: true },
  } satisfies MaskInputOptions,
};

// Default export for backwards compatibility
export default masks;
